import { useTranslate } from '@refinedev/core';
import { Card, Flex, Grid, Progress, Tabs, Tag, Typography, theme } from 'antd';
import type {
  PublicationListOut,
  PublicationProgressionDetailOut,
} from '@/api/generated/schemas';
import { StatCell } from '@/components/common/show/StatCell';
import { PublicationIconLink } from '@/components/user_catalog/publications/PublicationIconLink';
import { orDash } from '@/components/common/show/shared';
import {
  ConsumptionStatusTag,
  ProductFormatTag,
} from '@/components/progress/tags';
import { PROGRESSION_SHOW_TABS } from '../constants';
import type { ProgressionShowTab } from '../constants';

const { Text, Title } = Typography;

const TAB_LABEL_KEYS: Record<ProgressionShowTab, string> = {
  general: 'base.sections.basic_info',
};

interface ProgressionShowHeaderProps {
  record: PublicationProgressionDetailOut;
  publication?: PublicationListOut;
  activeTab: ProgressionShowTab;
  onTabChange: (key: string) => void;
}

export const ProgressionShowHeader = ({
  record,
  publication,
  activeTab,
  onTabChange,
}: ProgressionShowHeaderProps) => {
  const t = useTranslate();
  const { token } = theme.useToken();
  const screens = Grid.useBreakpoint();

  return (
    <Card styles={{ body: { paddingBottom: 0 } }}>
      <Flex gap={token.marginLG} align="flex-start" vertical={!screens.sm}>
        <div style={{ flex: 1, minWidth: 0 }}>
          {/* Long titles wrap instead of pushing the tabs bar out. */}
          <Title
            level={3}
            style={{
              margin: 0,
              fontSize: token.fontSizeHeading3,
              overflowWrap: 'anywhere',
            }}
          >
            <PublicationIconLink publicationId={record.publication_id}>
              {publication?.title ?? `#${record.publication_id}`}
            </PublicationIconLink>
          </Title>

          <Text
            type="secondary"
            style={{ display: 'block', marginTop: token.marginXXS }}
          >
            {`${t('progress.progressions.titles.show')} · ID ${record.id}`}
          </Text>

          <Flex
            gap={token.marginXL + token.margin}
            style={{ marginTop: token.marginLG }}
            wrap
          >
            <StatCell
              label={t('progress.progressions.fields.consumption_status')}
            >
              <ConsumptionStatusTag status={record.consumption_status} />
            </StatCell>
            <StatCell label={t('progress.progressions.fields.active_format')}>
              {/* Null until the profile opens the publication in some format. */}
              {orDash(
                record.active_format && (
                  <ProductFormatTag
                    value={record.active_format}
                    labels="progressions"
                  />
                )
              )}
            </StatCell>
            <StatCell label={t('progress.progressions.fields.progress')}>
              <Progress
                percent={record.progress}
                style={{ width: 180, marginBottom: 0 }}
              />
            </StatCell>
            <StatCell
              label={t(
                'progress.progressions.fields.should_display_end_dialog'
              )}
            >
              <Tag
                color={record.should_display_end_dialog ? 'blue' : 'default'}
              >
                {t(
                  `progress.progressions.end_dialog.${record.should_display_end_dialog ? 'yes' : 'no'}`
                )}
              </Tag>
            </StatCell>
          </Flex>
        </div>
      </Flex>

      <Tabs
        activeKey={activeTab}
        onChange={onTabChange}
        style={{ marginTop: token.marginSM }}
        tabBarStyle={{ marginBottom: 0 }}
        items={PROGRESSION_SHOW_TABS.map((key) => ({
          key,
          label: t(TAB_LABEL_KEYS[key]),
        }))}
      />
    </Card>
  );
};
